import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useNarrow } from '../../../lib/use-narrow';
import { useBasePath } from '../../../lib/use-base-path';
import { useAgentName } from '../../../lib/use-agent-name';
import { getRecentConversationId } from '../../../lib/conversations';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useDictation } from '../../../lib/use-dictation';
import { useVoiceNote } from '../../../lib/use-voice-note';
import { useVoiceCallEnabled } from '../../../lib/voice-call-flag';
import { useVoiceCallSummary } from '../../../lib/voice-call-presence-context';
import { cameraCaptureSupported } from '../../../lib/camera-capture';
import { CAMERA_CAPTURE_ACCEPT } from '../../../lib/attachments';
import { appendComposerLine, placeLine, shareTimeLine } from '../../../lib/composer-inserts';
import type { PlaceSummary } from '../../../lib/use-place-search';
import type { StickyChatTarget } from '../../../lib/sticky-chat-target';
import { ComposerPill } from '../ComposerPill';
import { ContactPicker } from '../chat-surface/ContactPicker';
import { PlacePicker } from '../chat-surface/PlacePicker';
import { TimePicker } from '../chat-surface/TimePicker';
import { AttachmentLimitNotice, ContactChipRow } from '../chat-surface/attachments';
import { useContactReferences } from '../chat-surface/MessagesPanel/use-contact-references';
import { StagedFileChips } from './StagedFileChips';
import { useStagedFiles } from './use-staged-files';

export { useHomeHandoff } from './use-home-handoff';

export interface HomeComposerProps {
  /** Caps the composer's width. `'none'` lets it STRETCH to fill its slot — the docked home bar. */
  maxWidth?: number | 'none';
  /** Rest as one line and open on focus — the docked bar on the Overview home. See `ComposerPill`. */
  collapsible?: boolean;
  autoFocus?: boolean;
  /** Overrides the field's placeholder — the sticky chat bar reads "Ask {maestro}", like on the phones. */
  placeholder?: string;
  conversationScope?: 'new' | 'calendar';
  /** The screen's own chat (the task page): sends continue it instead of starting a fresh one. */
  target?: StickyChatTarget | null;
  // Fired once a submit has handed off to the floating chat. The mobile launcher
  // uses it to dismiss itself immediately, so the now-empty composer (with its
  // disabled send arrow) doesn't linger over the keyboard during the route handoff.
  onSubmitted?: () => void;
}

/**
 * The home's new-conversation input. It is NOT the chat — submitting hands off to
 * the floating chat (which expands over the home and sends the message), so the
 * conversation lives in the one floating surface and this stays a plain launcher.
 */
export function HomeComposer({
  maxWidth = 760,
  collapsible = false,
  autoFocus,
  placeholder,
  conversationScope = 'new',
  target = null,
  onSubmitted,
}: HomeComposerProps) {
  const router = useRouter();
  const narrow = useNarrow();
  const basePath = useBasePath();
  const agent = useAgentName();
  const { t, i18n } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const {
    requestUpload,
    requestContacts,
    requestChatAction,
    openChatWithVoiceNote,
    openChatWithMessage,
    openCalendarChat,
    chatMode,
    activeConversationId,
    setActiveConversationId,
    setChatMode,
  } = useActiveConversation();
  const voiceCallEnabled = useVoiceCallEnabled();
  const [text, setText] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Dictation appends the transcript to the current draft for review/edit; it does
  // NOT auto-send.
  const dictation = useDictation((spoken) => {
    const trimmed = spoken.trim();
    if (!trimmed) return;
    setText((prev) => (prev.trim() ? `${prev.trim()} ${trimmed}` : trimmed));
  });
  // Log the composer's dictation controls for THIS (home) usage only, by wrapping
  // the dictation handlers before they reach the shared ComposerPill / RecordingBar.
  // Instrumenting inside ComposerPill/RecordingBar would also fire on the chat
  // (MessagesPanel) path, which is out of scope here.
  const loggedDictation: typeof dictation = {
    ...dictation,
    start: () => {
      logPageClickBi({ page_name: 'home', button_name: 'start_dictation' });
      dictation.start();
    },
    cancel: () => {
      logPageClickBi({ page_name: 'home', button_name: 'cancel_dictation' });
      dictation.cancel();
    },
    confirm: () => {
      logPageClickBi({ page_name: 'home', button_name: 'confirm_dictation' });
      dictation.confirm();
    },
  };

  // The mic's other mode: an empty draft records a VOICE NOTE, exactly as it does on both phones'
  // home composer (`homeMicTapped`). This is a launcher, not the chat — so it uploads the clip
  // here and hands the finished attachment to a FRESH chat, which auto-sends it with empty text.
  // Same destination native picks (`homeVoiceSendTapped` calls `newChat` rather than resuming).
  const voiceNote = useVoiceNote({
    onStart: () => logPageClickBi({ page_name: 'home', button_name: 'voice_note_start' }),
    onDiscard: () => logPageClickBi({ page_name: 'home', button_name: 'voice_note_delete' }),
    onUploadFailed: (message, mimeType) =>
      logPageClickBi({ page_name: 'home', button_name: 'attachment_upload_failed', state: mimeType, action: message.slice(0, 200) }),
    onSend: (attachment) => {
      logPageClickBi({ page_name: 'home', button_name: 'voice_note_sent' });
      openChatWithVoiceNote(attachment);
      onSubmitted?.();
    },
  });

  const { staged, stageFiles, removeAt, clear, limitNotice, refuseWhenFull } = useStagedFiles(chatMode === 'bubble');
  const onPickFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    stageFiles(Array.from(files));
  };

  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const [cameraAvailable, setCameraAvailable] = useState(false);
  useEffect(() => {
    setCameraAvailable(cameraCaptureSupported(typeof window === 'undefined' ? undefined : window));
  }, []);
  const openCamera = () => {
    if (refuseWhenFull()) return;
    cameraInputRef.current?.click();
  };

  const { pickedContacts, addContact, removeContact, clearContacts } = useContactReferences();
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [placePickerOpen, setPlacePickerOpen] = useState(false);
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const insertComposerLine = (line: string) => setText((prev) => appendComposerLine(prev, line));
  const onPickPlace = (place: PlaceSummary) => insertComposerLine(placeLine(place));
  const onPickTime = (date: Date) => insertComposerLine(shareTimeLine(date, i18n.language));

  // A send from the home composer starts a FRESH conversation, the rule both phones run
  // (`store.newChat(seedGreeting: false)` ahead of the send, in iOS `sendHomeInput` and Android
  // `ChatComposerFab`). Calendar is the exception: its persistent bar and item actions share the
  // Calendar-owned conversation while that screen remains open.
  //
  // Handed over through the STORE, not a `?prefill=…&autosend=1` URL. `useChatUrlSync` opens the chat
  // only on the TRANSITION into those params, so a handoff fired while they are already on
  // `router.query` — anything that strips them via `history.replaceState`, which Next's router never
  // sees, leaves them there — is not an edge and opens nothing. The store handoff sets the size and
  // the pending prefill outright, and needs no navigation: the chat already floats over this screen.
  const submit = () => {
    const trimmed = text.trim();
    const hasContacts = pickedContacts.length > 0;
    if (!trimmed && staged.length === 0 && !hasContacts) return;
    logPageClickBi({ page_name: 'home', button_name: 'send_message' });
    if (staged.length > 0) requestUpload(staged);
    if (hasContacts) requestContacts(pickedContacts);
    if (trimmed || hasContacts) {
      // A contact needs no upload step, so — unlike files — a contact-only draft (no typed text)
      // still autosends: the handoff carries an empty `message` and useAutosend appends the
      // contact's `[name]` token as the text.
      if (conversationScope === 'calendar') openCalendarChat(trimmed);
      else if (target?.conversationId) {
        openChatWithMessage({ kind: 'existing', conversationId: target.conversationId, message: trimmed, taskId: target.taskId });
      } else openChatWithMessage({ kind: 'new', message: trimmed, ...(target?.taskId ? { taskId: target.taskId } : {}) });
    } else if (target?.conversationId && conversationScope !== 'calendar') {
      // Files with no words, on a screen with its own chat: open that chat, and the upload stages there.
      setActiveConversationId(target.conversationId);
      setChatMode('expanded');
    } else {
      // Files with no words: nothing to autosend, so this one keeps the URL. It is not the store's
      // to express — a fresh-thread intent carrying no prefill leaves `isNewConv` false, and a
      // ChatSurface mounting for the first time would then seed the thread `?conversation=` still
      // names and stage the files onto it. `?new=1` both sets `isNewConv` and drops that param.
      // The URL's weakness does not apply here either: `requestUpload` above is what opens the chat
      // (`useChatUrlSync` watches `pendingUploadFiles`), not the handoff edge.
      void router.push('/?new=1', `${basePath}/?new=1`);
    }
    clear();
    clearContacts();
    setText('');
    onSubmitted?.();
  };
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit();
  };

  const canSend = text.trim().length > 0 || staged.length > 0 || pickedContacts.length > 0;

  // The composer is a launcher, not the chat, so it can't run a call itself (the voice infra lives in
  // the floating chat). It stashes the intent and opens the chat, which starts the call on mount —
  // the same `pendingChatAction` handshake MessagesPanel consumes. Unset when voice is off, so the
  // composer shows Send/dictate only.
  // A voice call opens the chat on the user's most recent thread — never a fresh `/?new=1`, whose
  // dismissed/failed call would strand them on an empty screen (the floating surface even suppresses
  // the greeting landing, so it reads as a blank chat). `activeConversationId` is resolved
  // asynchronously on mount and is often still null when the launcher is tapped, so we resolve the
  // recent thread directly here (`ChatSurface` seeds the store from `?conversation=…`). Only a
  // genuinely first-time user with no recent thread falls back to a fresh one.
  const RESUME_WINDOW_MS = 24 * 60 * 60 * 1000;
  const startCall = voiceCallEnabled
    ? () => {
        logPageClickBi({ page_name: 'home', button_name: 'start_voice_call' });
        requestChatAction('voice');
        const open = (id: string | null) => {
          if (id) void router.push(`/?conversation=${id}`, `${basePath}/?conversation=${id}`);
          else void router.push('/?new=1', `${basePath}/?new=1`);
          onSubmitted?.();
        };
        if (activeConversationId) open(activeConversationId);
        else void getRecentConversationId(RESUME_WINDOW_MS).then(open);
      }
    : undefined;
  // A call is already in progress (minimized island, or the surface itself) → don't offer to start
  // ANOTHER one: drop the "Speak" launcher so the composer shows Send instead.
  const voiceSummary = useVoiceCallSummary();
  const voiceCallUp = !!(voiceSummary?.timing.islandVisible || voiceSummary?.timing.surfaceVisible);

  return (
    <form
      onSubmit={onSubmit}
      style={{
        width: '100%',
        maxWidth,
        alignSelf: 'center',
        padding: narrow ? '0 16px' : 0,
        boxSizing: 'border-box',
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          onPickFiles(e.target.files);
          e.target.value = '';
        }}
      />
      {cameraAvailable && (
        <input
          ref={cameraInputRef}
          type="file"
          // Deliberately NOT `multiple`: a camera returns one take, and pairing the two makes Android
          // hand back a chooser instead of opening the camera (see Composer.tsx).
          accept={CAMERA_CAPTURE_ACCEPT}
          capture="environment"
          style={{ display: 'none' }}
          onChange={(e) => {
            onPickFiles(e.target.files);
            e.target.value = '';
          }}
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {limitNotice && <AttachmentLimitNotice />}
        <StagedFileChips files={staged} onRemove={removeAt} />
        {pickedContacts.length > 0 && <ContactChipRow contacts={pickedContacts} onRemove={removeContact} />}
        <ComposerPill
          collapsible={collapsible}
          value={text}
          onChange={setText}
          onSubmit={submit}
          placeholder={placeholder ?? t('home.composer.placeholderSymphony', { agentName: agent.short })}
          onAttach={() => {
            logPageClickBi({ page_name: 'home', button_name: 'attach_file' });
            if (refuseWhenFull()) return;
            fileInputRef.current?.click();
          }}
          attachLabel={t('chat.attach.menu')}
          attachTitle={t('chat.attach.menu')}
          onAttachContact={() => setContactPickerOpen(true)}
          chooseFileLabel={t('chat.attach.file')}
          contactLabel={t('chat.attach.contact')}
          onTakePhoto={cameraAvailable ? openCamera : undefined}
          takePhotoLabel={t('chat.attach.takePhotoOrVideo')}
          onAttachLocation={() => setPlacePickerOpen(true)}
          locationLabel={t('chat.attach.location')}
          onAttachTime={() => setTimePickerOpen(true)}
          shareTimeLabel={t('chat.attach.shareTime')}
          dictation={loggedDictation}
          dictateLabel={t('chat.dictate')}
          transcribingLabel={t('chat.dictate.transcribing')}
          cancelLabel={t('common.cancel')}
          voiceNote={voiceNote}
          recordVoiceNoteLabel={t('chat.voice.recordVoiceNote')}
          discardRecordingLabel={t('chat.voice.discard')}
          sendVoiceNoteLabel={t('chat.voice.sendNote')}
          onStartCall={voiceCallUp ? undefined : startCall}
          startCallLabel={t('home.composer.startCall')}
          speakLabel={t('home.composer.speak')}
          // The FAB opens auto-focused, so keep the Speak pill on the empty field (it would otherwise
          // only ever show the disabled Send arrow) — matching the chat composer's resting Speak.
          speakWhenEmpty
          canSend={canSend}
          sendLabel={t('common.send')}
          autoFocus={autoFocus}
        />
      </div>
      {contactPickerOpen && <ContactPicker onPick={addContact} onClose={() => setContactPickerOpen(false)} />}
      {placePickerOpen && <PlacePicker onPick={onPickPlace} onClose={() => setPlacePickerOpen(false)} />}
      {timePickerOpen && <TimePicker onPick={onPickTime} onClose={() => setTimePickerOpen(false)} />}
    </form>
  );
}
